<template>
  <div class="settings-field" :class="{ _row: isRow, _disabled: disabled }">
    <div class="settings-field__meta">
      <label class="settings-field__label" :for="labelFor">
        {{ label }}
        <span v-if="tooltip" class="settings-field__tooltip" v-tooltip="{ content: tooltip, html: tooltipHtml, triggers: ['hover', 'click'] }">
          <svg v-svg symbol="info" size="0 0 18 18" role="info"></svg>
        </span>
      </label>
      <p v-if="hint" class="settings-field__hint">{{ hint }}</p>
    </div>
    <div class="settings-field__control">
      <div v-if="readonly" class="settings-field__readonly">
        <span class="settings-field__value">{{ readonlyValue || "—" }}</span>
        <span class="settings-field__lock">
          <SatIcon name="lock" :size="13" />
          {{ $t("platform.card.readonly") }}
        </span>
      </div>
      <slot v-else />
      <small v-if="error" class="error settings-field__error">{{ error }}</small>
      <slot name="after" />
    </div>
  </div>
</template>
<script>
import SatIcon from "@/components/Satellites/SatIcon.vue";

// One setting of a platform: its name and what it is for on one side, the control on the other. The card lays the
// two out in a row (`settingsFieldRow` from the page); the create wizard keeps them stacked in its narrow column.
export default {
  name: "SettingsField",
  components: { SatIcon },
  inject: {
    settingsFieldRow: { default: false },
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    hint: {
      type: String,
      default: "",
    },
    // Details or an example, behind the info icon
    tooltip: {
      type: String,
      default: "",
    },
    tooltipHtml: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
    // A value set when the platform was created: shown as text, not as a control
    readonly: {
      type: Boolean,
      default: false,
    },
    readonlyValue: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    labelFor: {
      type: String,
      default: null,
    },
    row: {
      type: Boolean,
      default: null,
    },
  },
  computed: {
    isRow() {
      return this.row === null ? this.settingsFieldRow : this.row;
    },
  },
};
</script>
<style lang="scss" scoped>
.settings-field {
  padding: 16px 0;

  & + & {
    border-top: 1px solid #eeeef3;
  }

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &._row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
    gap: 8px 32px;

    @media (max-width: 767px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  &._disabled .settings-field__control {
    opacity: 0.6;
  }
}

.settings-field__meta {
  min-width: 0;
  margin-bottom: 10px;

  ._row > & {
    margin-bottom: 0;
  }
}

.settings-field__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: #141414;
}

.settings-field__tooltip {
  display: inline-flex;
  cursor: pointer;

  svg {
    fill: #5183cf;
  }
}

.settings-field__hint {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: #828289;
}

.settings-field__control {
  min-width: 0;

  ::v-deep .form-item {
    padding-bottom: 0;
  }

  ::v-deep .form-item__field {
    margin-bottom: 0;
  }
}

.settings-field__readonly {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  min-height: 44px;
}

.settings-field__value {
  font-size: 15px;
  font-weight: 600;
  word-break: break-word;
  color: #141414;
}

.settings-field__lock {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #9b9ba4;
}

.settings-field__error {
  display: block;
  margin-top: 6px;
}

body.dark {
  .settings-field + .settings-field {
    border-top-color: #343438;
  }

  .settings-field__label,
  .settings-field__value {
    color: #dddddd;
  }

  .settings-field__hint {
    color: #9b9ba4;
  }
}
</style>
